// Архив матчей. Источник — /api/matches/history (последние 50 матчей).
// Тап по матчу → /replay/:id (пошаговый просмотр).

import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScreenLayout } from '../components/ScreenLayout';
import { useTelegram } from '../telegram/useTelegram';
import { PlayerName } from '../components/PlayerName';
import { useTooltipFlow } from '../tutorial/useTooltipFlow';
import { useTr } from '../i18n/useTr';
import { getMatchHistory, type MatchHistoryItem } from '../api/client';
import styles from './ArchiveScreen.module.css';

type MatchOutcome = 'win' | 'loss' | 'draw';
type Filter = 'all' | 'win' | 'loss';

interface MatchRecord {
  id: number;
  date: string;     // '06/05/26'
  time: string;     // '22:30'
  outcome: MatchOutcome;
  duration: string; // '7 мин 14 с'
  opponent: {
    nick: string;
    faction: 'deus' | 'syntetics' | 'global' | 'livlife';
    display_name?: string;
    accent_color?: string | null;
    title?: { title_code: string; title_text_ru: string } | null;
  };
  hero: string;
  ratingDelta: number;
  scriptsDelta: number;
}

function fmtDate(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear() % 100).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mn = String(d.getMinutes()).padStart(2, '0');
  return { date: `${dd}/${mm}/${yy}`, time: `${hh}:${mn}` };
}
function fmtDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m} мин ${s} с` : `${s} с`;
}
function mapHistoryToRecord(h: MatchHistoryItem): MatchRecord {
  const { date, time } = fmtDate(h.createdAt);
  const oppFaction = (h.opponent?.faction || 'deus') as MatchRecord['opponent']['faction'];
  const validFaction = ['deus','syntetics','global','livlife'].includes(oppFaction) ? oppFaction : 'deus';
  return {
    id: h.id,
    date, time,
    outcome: h.result as MatchOutcome,
    duration: fmtDuration(h.durationSec || 0),
    opponent: {
      nick: h.opponent?.nick || 'бот',
      faction: validFaction,
      display_name: h.opponent?.display_name,
      accent_color: h.opponent?.accent_color ?? null,
      title: h.opponent?.title ?? null,
    },
    hero: h.heroId,
    ratingDelta: h.mmrDelta || 0,
    scriptsDelta: h.scriptsEarned || 0,
  };
}

const FACTION_LABELS: Record<MatchRecord['opponent']['faction'], string> = {
  deus: 'Деус', syntetics: 'Синтетикс', global: 'Глобал', livlife: 'Лив Лайф'
};
const FACTION_LABELS_EN: Record<MatchRecord['opponent']['faction'], string> = {
  deus: 'Deus', syntetics: 'Synthetics', global: 'Global', livlife: 'Liv Life'
};

export function ArchiveScreen() {
  const navigate = useNavigate();
  const { hapticTap } = useTelegram();
  const [filter, setFilter] = useState<Filter>('all');

  // ФТУЕ.6: tooltip-флоу первого входа в Архив матчей.
  const tr = useTr();
  useTooltipFlow('archive', () => [
    {
      id: 'arch-intro',
      coach: {
        title: tr('Архив матчей', 'Match archive'),
        body: tr(
          'Тут все твои бои за последние 30 дней. Каждая запись: дата, противник (ник + корпа + лига), Герой обеих сторон, результат, длительность и delta MMR/Скр.',
          'All your battles from the last 30 days. Each entry: date, opponent (handle + corp + league), Hero of both sides, result, duration, and MMR/Scripts delta.'
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'arch-filters',
      coach: {
        title: tr('Фильтры', 'Filters'),
        body: tr(
          'Сверху — переключатели «Все / Победы / Поражения». Полезно когда нужно посмотреть проигранные подряд и сделать выводы по составу колоды.',
          'Top tabs «All / Wins / Losses». Handy to look at recent losses and learn from your deck composition.'
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'arch-replay',
      coach: {
        title: tr('Реплеи и жалобы', 'Replays & reports'),
        body: tr(
          'Тап по матчу → детали. Можно посмотреть реплей и подать жалобу (читы / токсичность). Реплеи без БП — ограниченно; с БП — все за 30 дней.',
          'Tap a match → details. You can watch a replay and report (cheats / toxicity). Replays without BP — limited; with BP — all 30 days.'
        ),
        showNext: true, showSkip: false,
        nextLabel: tr('Понял', 'Got it')
      }
    }
  ]);

  const [matches, setMatches] = useState<MatchRecord[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    getMatchHistory(50)
      .then((rows) => {
        if (cancelled) return;
        setMatches(rows.map(mapHistoryToRecord));
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const counts = useMemo(() => ({
    all:  matches.length,
    win:  matches.filter(m => m.outcome === 'win').length,
    loss: matches.filter(m => m.outcome === 'loss').length
  }), [matches]);

  const filtered = useMemo(() => {
    if (filter === 'all') return matches;
    return matches.filter(m => m.outcome === filter);
  }, [filter, matches]);

  const onMatch = (id: number) => () => {
    hapticTap();
    // РЕП.1: тап по матчу — сразу в реплей. Если replay нет, ReplayScreen
    // покажет fallback (текст-лог или плейсхолдер).
    navigate(`/replay/${id}`);
  };

  const isEn = tr('ru', 'en') === 'en';
  const factionLabels = isEn ? FACTION_LABELS_EN : FACTION_LABELS;
  return (
    <ScreenLayout title={tr('АРХИВ', 'ARCHIVE')} backTo="/profile">
      <div className={styles.infoBlock}>
        ► <strong>RETENTION 30D</strong> · {tr('хранятся 30 дней', 'kept for 30 days')} · {tr('реплеи', 'replays')}:{' '}
        <span className={styles.f2pTag}>{tr('1 ПРОСМОТР В СУТКИ', '1 VIEW PER DAY')}</span>
      </div>

      <div className={styles.filters}>
        <FilterPill label={tr('Все', 'All')}      num={counts.all}  active={filter === 'all'}  onClick={() => setFilter('all')} />
        <FilterPill label={tr('Победы', 'Wins')}   num={counts.win}  active={filter === 'win'}  onClick={() => setFilter('win')} />
        <FilterPill label={tr('Пораж.', 'Losses')}   num={counts.loss} active={filter === 'loss'} onClick={() => setFilter('loss')} />
      </div>

      <div className={styles.matchesList}>
        {filtered.map(m => (
          <button key={m.id} className={`${styles.matchRow} cut-s`} onClick={onMatch(m.id)}>
            <div className={`${styles.matchOutcome} ${styles[`outcome_${m.outcome}`]}`}>
              {m.outcome === 'win'
                ? tr('▲ ПОБЕДА', '▲ WIN')
                : m.outcome === 'loss'
                  ? tr('▼ ПОРАЖ.', '▼ LOSS')
                  : tr('◇ НИЧЬЯ', '◇ DRAW')}
            </div>
            <div className={styles.matchMeta}>
              <div className={styles.matchOpponent}>
                vs <PlayerName
                  name={m.opponent.display_name || m.opponent.nick}
                  accentColor={m.opponent.accent_color ?? undefined}
                  title={m.opponent.title}
                  size="sm"
                  inline
                  style={{ color: `var(--${m.opponent.faction === 'syntetics' ? 'syn' : m.opponent.faction === 'global' ? 'gsec' : m.opponent.faction === 'livlife' ? 'liv' : 'deus'})` }}
                />
                <span className={styles.matchFaction}> · {factionLabels[m.opponent.faction]}</span>
              </div>
              <div className={styles.matchDetails}>
                {m.date} · {m.time} · {m.hero} · {m.duration}
              </div>
            </div>
            <div className={styles.matchDelta}>
              <span className={m.ratingDelta > 0 ? styles.deltaPlus : styles.deltaMinus}>
                {m.ratingDelta > 0 ? '+' : ''}{m.ratingDelta}
              </span>
              <span className={styles.deltaSub}>+{m.scriptsDelta} ◆</span>
            </div>
          </button>
        ))}
        {loading && (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--t-mute)', fontSize: 12 }}>
            {tr('Загрузка истории матчей…', 'Loading match history…')}
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--t-md)', fontSize: 12 }}>
            {tr(
              'Матчей пока нет. Сыграй первый — он появится здесь.',
              "No matches yet. Play the first one — it'll appear here."
            )}
          </div>
        )}
      </div>
    </ScreenLayout>
  );
}

function FilterPill({ label, num, active, onClick }: { label: string; num: number; active: boolean; onClick: () => void }) {
  return (
    <button
      className={`${styles.pill} ${active ? styles.pillActive : ''} cut-s`}
      onClick={onClick}
    >
      {label} <span className={styles.pillNum}>{num}</span>
    </button>
  );
}
